/* ---------------------------------------------------------------------------
   Loading a division's approved district rates.

   `QC-06`, and the only way a price gets into this register.

   THERE ARE NO BUILT-IN RATES. A Nepali district rate is approved per division
   per fiscal year in an office this repository has never seen, and inventing a
   plausible one would be worse than shipping none: the figure would come back
   in a budget submission quoted by somebody who had no reason to doubt it. So
   the register starts empty, every estimate says so in as many words, and this
   is where a Division's own table goes in.

   PASTED, LIKE THE INVENTORY IMPORT, AND FOR THE SAME REASON. The thing that
   actually exists in a Division office is a spreadsheet. A file picker that
   accepts one format and a paste box that accepts what is on the clipboard are
   the same amount of code; the paste box is the one that works when the
   spreadsheet is open on the screen beside this.

   EVERY FAULT AT ONCE. `validateRateTable` returns a list rather than the first
   problem, and this prints the list. One bad row at a time turns a ten-minute
   job into ten round trips for somebody who did not write this code.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { button, field, scrim } from '../components/widgets.ts';
import { npr } from '../data/estimate.ts';
import { ratableTreatments } from '../data/rates.ts';
import { wordingMap } from '../data/remedial.ts';
import { daysSinceApproval, RATE_TABLE_STALE_DAYS } from '../data/rates.ts';

/** The dialog, or null when it is closed. */
export function ratesDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.ratesOpen) return null;

  const today = new Date().toISOString().slice(0, 10);

  return h(
    'div',
    {
      class: 'dlg',
      ...scrim(() => actions.closeRates(), () => s.ratesSaving),
    },
    h(
      'form',
      {
        class: 'dlg__panel dlg__panel--sm dlg__panel--compact',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': 'District rates',
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.submitRateTable();
        },
      },

      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', { class: 'dlg__kicker', text: 'divisional district rates · approved annually' }),
          h('h2', { class: 'dlg__title', text: 'District rates' }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Close',
            'aria-label': 'Close',
            onclick: () => actions.closeRates(),
          },
          '×',
        ),
      ),

      s.ratesFaults.length
        ? h(
            'div',
            { class: 'dlg__note dlg__note--error' },
            h('div', { text: `${s.ratesFaults.length} problem${s.ratesFaults.length === 1 ? '' : 's'}:` }),
            ...s.ratesFaults.map((f) => h('div', { class: 'rates__fault', text: `· ${f}` })),
          )
        : null,

      h(
        'div',
        { class: 'dlg__body' },

        /* What is already loaded, first. The commonest reason somebody opens
           this is to find out whether the district they are looking at has
           rates at all — and the second commonest is to discover that the only
           table loaded is last year's. */
        h(
          'div',
          { class: 'dlg__block' },
          h('div', { class: 'dlg__block-cap', text: 'Loaded' }),
          s.rateTables.length
            ? h(
                'table',
                { class: 'tbl' },
                h(
                  'thead',
                  {},
                  h(
                    'tr',
                    {},
                    h('th', { text: 'District' }),
                    h('th', { style: 'width:88px', text: 'Fiscal year' }),
                    h('th', { style: 'width:110px', text: 'Approved' }),
                    h('th', { text: 'By' }),
                    h('th', { style: 'width:60px', text: 'Rates' }),
                    h('th', { style: 'width:70px' }),
                  ),
                ),
                h(
                  'tbody',
                  {},
                  ...s.rateTables.map((t) => {
                    const age = daysSinceApproval(t, today);
                    const stale = age !== null && age > RATE_TABLE_STALE_DAYS;
                    return h(
                      'tr',
                      {},
                      h('td', { text: t.district }),
                      h('td', { class: 'num', text: t.fiscalYear }),
                      h('td', {
                        class: ['num', 'nowrap', stale && 'rates__stale'],
                        title: stale
                          ? `Approved ${age} days ago. District rates are approved annually, so this is very likely not the current table.`
                          : '',
                        text: t.approvedOn,
                      }),
                      h('td', { class: 'ellipsis', title: t.approvedBy, text: t.approvedBy }),
                      h('td', { class: 'num', text: String(t.rows.length) }),
                      h(
                        'td',
                        {},
                        button('Withdraw', {
                          sm: true,
                          danger: true,
                          onClick: () => void actions.withdrawRateTable(t.id),
                        }),
                      ),
                    );
                  }),
                ),
              )
            : h('div', {
                class: 'reg__empty',
                text:
                  'No approved rate table has been loaded. Every estimate in this register ' +
                  'reports that nothing is priced, which is the correct answer until one is.',
              }),
        ),

        h(
          'div',
          { class: 'dlg__form-grid' },
          field(
            'District',
            h('input', {
              id: 'rate-district',
              class: 'input',
              required: true,
              placeholder: 'As it is written on the record',
              value: s.ratesDraft.district,
              oninput: (e: Event) =>
                actions.setRatesField('district', (e.target as HTMLInputElement).value),
            }),
            'rate-district',
          ),
          field(
            'Fiscal year',
            h('input', {
              id: 'rate-fy',
              class: 'input',
              required: true,
              placeholder: '2081/82',
              value: s.ratesDraft.fiscalYear,
              oninput: (e: Event) =>
                actions.setRatesField('fiscalYear', (e.target as HTMLInputElement).value),
            }),
            'rate-fy',
          ),
          field(
            'Approved on',
            h('input', {
              id: 'rate-on',
              class: 'input',
              type: 'date',
              required: true,
              value: s.ratesDraft.approvedOn,
              oninput: (e: Event) =>
                actions.setRatesField('approvedOn', (e.target as HTMLInputElement).value),
            }),
            'rate-on',
          ),
          field(
            'Approved by',
            h('input', {
              id: 'rate-by',
              class: 'input',
              required: true,
              placeholder: 'The office that approved it',
              value: s.ratesDraft.approvedBy,
              oninput: (e: Event) =>
                actions.setRatesField('approvedBy', (e.target as HTMLInputElement).value),
            }),
            'rate-by',
          ),
          field(
            'Contingency %',
            h('input', {
              id: 'rate-cont',
              class: 'input',
              type: 'number',
              step: 'any',
              min: '0',
              max: '100',
              placeholder: '—',
              value: s.ratesDraft.contingencyPct,
              oninput: (e: Event) =>
                actions.setRatesField('contingencyPct', (e.target as HTMLInputElement).value),
            }),
            'rate-cont',
          ),
          field(
            'VAT %',
            h('input', {
              id: 'rate-vat',
              class: 'input',
              type: 'number',
              step: 'any',
              min: '0',
              max: '100',
              placeholder: '—',
              value: s.ratesDraft.vatPct,
              oninput: (e: Event) =>
                actions.setRatesField('vatPct', (e.target as HTMLInputElement).value),
            }),
            'rate-vat',
          ),
        ),

        h(
          'div',
          { class: 'dlg__block' },
          field(
            'Rates',
            h('textarea', {
              id: 'rate-rows',
              class: 'input',
              rows: 8,
              placeholder: 'crk-resin-injection\tm\t850\t...',
              value: s.ratesDraft.rows,
              oninput: (e: Event) =>
                actions.setRatesField('rows', (e.target as HTMLTextAreaElement).value),
            }),
            'rate-rows',
          ),
        ),

        /* The item ids, with the unit each is billed in — nobody can guess
           `sco-bed-protection`. */
        h(
          'div',
          { class: 'dlg__block' },
          h('div', { class: 'dlg__block-cap', text: 'Items this register can cost' }),
          h(
            'div',
            { class: 'rates__items' },
            ...ratableTreatments(wordingMap(s.treatmentWording)).map((t) =>
              h(
                'div',
                { class: 'rates__item' },
                h('code', { class: 'rates__id', text: t.id }),
                h('span', { class: 'rates__unit', text: t.unit }),
                h('span', { class: 'rates__label ellipsis', text: t.label }),
              ),
            ),
          ),
        ),
      ),

      h(
        'div',
        { class: 'dlg__foot' },
        h('span', {
          class: 'dlg__foot-note',
          text: s.rateTables.length
            ? `${s.rateTables.reduce((n, t) => n + t.rows.length, 0)} rates loaded · totals shown as ${npr(1234567)}`
            : '',
        }),
        h('span', { class: 'spacer' }),
        button('Close', { onClick: () => actions.closeRates(), disabled: s.ratesSaving }),
        button(s.ratesSaving ? 'Loading…' : 'Load rates', {
          type: 'submit',
          primary: true,
          disabled: s.ratesSaving,
        }),
      ),
    ),
  );
}
